import type { NestedSelectOption } from '@/types/form'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import type { AnswerInputProps } from '../QuestionRenderer'

type NestedSelectValue = Record<string, string | null>

function optionListFromQuestion(question: AnswerInputProps<NestedSelectValue>['question']) {
  if (question.settings.nestedOptions?.length) return question.settings.nestedOptions
  return question.choices.map<NestedSelectOption>((choice) => ({
    id: choice.id,
    label: choice.label,
    value: choice.value,
    children: [],
  }))
}

function findOption(options: NestedSelectOption[], selectedValue: string | null | undefined) {
  return options.find((option) => option.value === selectedValue)
}

function optionsForLevel(
  rootOptions: NestedSelectOption[],
  value: NestedSelectValue,
  level: number,
) {
  if (level === 0) return rootOptions
  let options = rootOptions
  for (let index = 0; index < level; index += 1) {
    const selected = findOption(options, value[`level-${index}`])
    if (!selected) return []
    options = selected.children ?? []
  }
  return options
}

export function NestedSelectInput({
  question,
  value = {},
  onChange,
  onSubmit,
}: AnswerInputProps<NestedSelectValue>) {
  const levels = question.settings.nestedLevels ?? ['第一層', '第二層']
  const rootOptions = optionListFromQuestion(question)
  const selectedLabels = getSelectedLabels(rootOptions, value, levels.length)

  function choose(level: number, nextValue: string) {
    const levelOptions = optionsForLevel(rootOptions, value, level)
    const next: NestedSelectValue = { ...value, [`level-${level}`]: nextValue }
    for (let index = level + 1; index < levels.length; index += 1) {
      next[`level-${index}`] = null
    }
    onChange(next)

    const selected = findOption(levelOptions, nextValue)
    const hasChildren = Boolean(selected?.children?.length)
    if (!hasChildren || level >= levels.length - 1) {
      window.setTimeout(onSubmit, 140)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          'grid grid-cols-1 gap-4',
          levels.length === 2 && 'md:grid-cols-2',
          levels.length >= 3 && 'md:grid-cols-3',
        )}
      >
        {levels.map((levelLabel, level) => {
          const options = optionsForLevel(rootOptions, value, level)
          const previousSelected = level === 0 || Boolean(value[`level-${level - 1}`])
          return (
            <OptionColumn
              key={levelLabel}
              label={levelLabel}
              options={options}
              value={value[`level-${level}`]}
              emptyText={previousSelected ? '此選項沒有下一層' : `請先選擇${levels[level - 1]}`}
              onChoose={(nextValue) => choose(level, nextValue)}
            />
          )
        })}
      </div>
      {selectedLabels.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          目前選擇：{selectedLabels.join(' / ')}
        </div>
      )}
    </div>
  )
}

function getSelectedLabels(
  rootOptions: NestedSelectOption[],
  value: NestedSelectValue,
  levelCount: number,
) {
  const labels: string[] = []
  let options = rootOptions
  for (let level = 0; level < levelCount; level += 1) {
    const selected = findOption(options, value[`level-${level}`])
    if (!selected) break
    labels.push(selected.label)
    options = selected.children ?? []
  }
  return labels
}

function OptionColumn({
  label,
  options,
  value,
  emptyText = '尚無選項',
  onChoose,
}: {
  label: string
  options: NestedSelectOption[]
  value: string | null | undefined
  emptyText?: string
  onChoose: (value: string) => void
}) {
  return (
    <div
      className="rounded-lg border p-3"
      style={{
        background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 86%, white)',
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
        borderRadius: 'var(--form-radius,8px)',
      }}
    >
      <div className="mb-2 text-sm font-medium" style={{ color: 'var(--form-answer,#64748B)' }}>
        {label}
      </div>
      <div className="space-y-2">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onChoose(option.value)}
            className="flex min-h-11 w-full items-center justify-between rounded-md border px-3 text-left"
            style={{
              background:
                value === option.value
                  ? 'color-mix(in srgb, var(--form-accent,#6366F1) 15%, white)'
                  : 'transparent',
              borderColor:
                value === option.value
                  ? 'var(--form-accent,#6366F1)'
                  : 'color-mix(in srgb, var(--form-accent,#6366F1) 18%, transparent)',
              color: 'var(--form-answer,#334155)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            }}
          >
            {option.label}
            {value === option.value && (
              <Check className="size-4" style={{ color: 'var(--form-accent,#6366F1)' }} />
            )}
          </button>
        ))}
        {options.length === 0 && <div className="py-4 text-sm text-slate-500">{emptyText}</div>}
      </div>
    </div>
  )
}
